import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@neo/primitives/collapsible'
import { ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'
import { EXAMPLE_REQUEST, EXAMPLE_RESPONSE, SIP_HEADER } from './handoff-context.helpers'

const Step = ({ mark, title, children }: { mark: string; title: string; children: ReactNode }) => (
  <li className="flex gap-3">
    <span className="flex size-5 shrink-0 items-center justify-center rounded-full border border-st2 font-jetbrains text-xxs-600 text-secondary">
      {mark}
    </span>
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-s-500 text-primary">{title}</span>
      <div className="text-s-400 text-secondary">{children}</div>
    </div>
  </li>
)

const Code = ({ children }: { children: ReactNode }) => (
  <code className="break-all rounded bg-bg2 px-1 font-jetbrains text-xs-400 text-primary">
    {children}
  </code>
)

const Disclosure = ({ label, children }: { label: string; children: ReactNode }) => (
  <Collapsible>
    <CollapsibleTrigger className="mt-1 flex items-center gap-1 text-left text-xs-500 text-brand-text [&[data-state=open]>svg]:rotate-90">
      <ChevronRight className="size-3.5 shrink-0 transition-transform" />
      {label}
    </CollapsibleTrigger>
    <CollapsibleContent>{children}</CollapsibleContent>
  </Collapsible>
)

const Example = ({ label, text }: { label: string; text: string }) => (
  <Disclosure label={label}>
    <div className="group relative mt-2">
      <pre className="overflow-x-auto rounded-md bg-bg2 p-3 font-jetbrains text-xs-400 text-primary">
        {text}
      </pre>
      <ClipboardCopyButton
        textToCopy={text}
        aria-label={`Copy ${label.toLowerCase()}`}
        tooltipText="Copy"
        position="topRight"
      />
    </div>
  </Disclosure>
)

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="flex flex-col gap-3">
    <span className="text-s-500 text-primary">{title}</span>
    <ol className="flex flex-col gap-4">{children}</ol>
  </div>
)

/** Both ways a call reaches the human specialist: the Flow keeps it, or the AI agent transfers it in. */
export const ConnectionSteps = ({ url }: { url: string }) => (
  <div className="flex flex-col gap-5 border-t border-st2 pt-4">
    <Section title="When the customer calls your Plivo CX number">
      <Step mark="1" title="Send the call to your AI agent">
        In this Flow, a Call Forward step dials your AI agent&apos;s SIP address with{' '}
        <span className="text-primary">Send a transfer ID to the AI agent</span> on. The ID arrives
        in the SIP header <Code>{SIP_HEADER}</Code>.
      </Step>
      <Step mark="2" title="Hand off">
        POST the transfer context to the endpoint with that <Code>handoff_id</Code> and the
        caller&apos;s number as <Code>original_caller</Code>, without <Code>transfer_to</Code> or{' '}
        <Code>transfer_from</Code>, then hang up. The call continues to Assign to human.
      </Step>
    </Section>
    <Section title="When the customer calls your AI agent's number">
      <Step mark="1" title="Send the transfer context">
        POST it to the endpoint with <Code>transfer_to</Code>: your Plivo CX number you will
        transfer the call to, and <Code>transfer_from</Code>: the caller ID your transfer call will
        show. Pick your own <Code>handoff_id</Code> so you can safely resend the request. The number
        is held for this call for 2 minutes.
        <Example label="Example request" text={EXAMPLE_REQUEST} />
        <Example label="Example response" text={EXAMPLE_RESPONSE} />
      </Step>
      <Step mark="2" title="Transfer the call">
        After a 202 or 200, transfer it to <Code>transfer_to</Code> from <Code>transfer_from</Code>,
        by phone or SIP. On a 503 or no response, resend the same request after 2 seconds. Without a
        202 or 200, don&apos;t transfer to that number.
        <Disclosure label="Optional: stay on the call until a human specialist answers">
          <p className="mt-1">
            If your platform keeps its leg during the transfer, check{' '}
            <Code>GET {url}/&lt;handoff ID&gt;/status</Code> every 1–2 seconds and leave the call at{' '}
            <Code>accepted</Code>. <Code>ended</Code> means no human answered. Stop at your own
            deadline: <Code>waiting</Code> can last forever.
          </p>
        </Disclosure>
      </Step>
    </Section>
    <div className="flex flex-col gap-2 text-s-400 text-secondary">
      <p>
        Every request uses Basic auth: the Auth ID as username, the Auth Token as password. To
        update the context, send every field again with the same <Code>handoff_id</Code> and{' '}
        <Code>revision</Code> + 1.
      </p>
    </div>
  </div>
)
